import React, { createContext, useContext, useReducer, ReactNode } from 'react';

export interface WidgetState {
  id: string;
  type: string;
  position: { x: number; y: number };
  zIndex: number;
  visible: boolean;
}

type WidgetAction =
  | { type: 'OPEN_WIDGET'; payload: { id: string; widgetType: string; position?: { x: number; y: number } } }
  | { type: 'CLOSE_WIDGET'; payload: { id: string } }
  | { type: 'FOCUS_WIDGET'; payload: { id: string } }
  | { type: 'MOVE_WIDGET'; payload: { id: string; position: { x: number; y: number } } };

interface WidgetManagerState {
  widgets: WidgetState[];
  highestZIndex: number;
}

interface WidgetManagerContextType {
  widgets: WidgetState[];
  openWidget: (id: string, widgetType: string, position?: { x: number; y: number }) => void;
  closeWidget: (id: string) => void;
  focusWidget: (id: string) => void;
  moveWidget: (id: string, position: { x: number; y: number }) => void;
  isWidgetOpen: (id: string) => boolean;
}

const WidgetManagerContext = createContext<WidgetManagerContextType | undefined>(undefined);

const widgetReducer = (state: WidgetManagerState, action: WidgetAction): WidgetManagerState => {
  switch (action.type) {
    case 'OPEN_WIDGET': {
      const existingWidget = state.widgets.find(w => w.id === action.payload.id);
      if (existingWidget) {
        return {
          ...state,
          widgets: state.widgets.map(w => 
            w.id === action.payload.id 
              ? { ...w, visible: true, zIndex: state.highestZIndex + 1 }
              : w
          ),
          highestZIndex: state.highestZIndex + 1
        };
      }

      const newZIndex = state.highestZIndex + 1;
      const defaultPosition = action.payload.position || { x: 100, y: 100 };
      
      return {
        ...state,
        widgets: [...state.widgets, {
          id: action.payload.id,
          type: action.payload.widgetType,
          position: defaultPosition,
          zIndex: newZIndex,
          visible: true
        }],
        highestZIndex: newZIndex
      };
    }

    case 'CLOSE_WIDGET':
      return {
        ...state,
        widgets: state.widgets.map(w => 
          w.id === action.payload.id ? { ...w, visible: false } : w
        )
      };

    case 'FOCUS_WIDGET': {
      const newZIndex = state.highestZIndex + 1;
      return {
        ...state,
        widgets: state.widgets.map(w => 
          w.id === action.payload.id ? { ...w, zIndex: newZIndex } : w
        ),
        highestZIndex: newZIndex
      };
    }

    case 'MOVE_WIDGET':
      return {
        ...state,
        widgets: state.widgets.map(w => 
          w.id === action.payload.id 
            ? { ...w, position: action.payload.position }
            : w
        )
      };

    default:
      return state;
  }
};

export const useWidgetManager = () => {
  const context = useContext(WidgetManagerContext);
  if (!context) {
    throw new Error('useWidgetManager must be used within a WidgetManagerProvider');
  }
  return context;
};

interface WidgetManagerProviderProps {
  children: ReactNode;
}

export const WidgetManagerProvider: React.FC<WidgetManagerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(widgetReducer, {
    widgets: [],
    highestZIndex: 1000
  });

  const openWidget = (id: string, widgetType: string, position?: { x: number; y: number }) => {
    dispatch({ type: 'OPEN_WIDGET', payload: { id, widgetType, position } });
  };

  const closeWidget = (id: string) => {
    dispatch({ type: 'CLOSE_WIDGET', payload: { id } });
  };

  const focusWidget = (id: string) => {
    dispatch({ type: 'FOCUS_WIDGET', payload: { id } });
  };

  const moveWidget = (id: string, position: { x: number; y: number }) => {
    dispatch({ type: 'MOVE_WIDGET', payload: { id, position } });
  };

  const isWidgetOpen = (id: string) => {
    const widget = state.widgets.find(w => w.id === id);
    return widget ? widget.visible : false;
  };

  return (
    <WidgetManagerContext.Provider value={{
      widgets: state.widgets,
      openWidget,
      closeWidget,
      focusWidget,
      moveWidget,
      isWidgetOpen
    }}>
      {children}
    </WidgetManagerContext.Provider>
  );
};